<script>
import { computed, defineComponent, ref, toRefs } from "vue";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import { AnswerStateEnum, QuizStateEnum } from "@/common/composables/use-vocab-quiz";
import IconCorrect from "@/components/icons/IconCorrect.vue";
import IconIncorrect from "@/components/icons/IconIncorrect.vue";
import { Autofocus } from "@/directives";
import { i18n } from "@/i18n";

import { useGameComponent } from "./common";

const { commonProps, commonEmits } = useGameComponent();

export default defineComponent({
  components: { IconCorrect, IconIncorrect, TgButton },
  directives: { Autofocus },
  props: commonProps,
  emits: commonEmits,
  /** @param {import("./common").Props} props */
  setup(props, { emit }) {
    const { t } = i18n.global;
    const { promptId, questions, quizState } = toRefs(props);

    const answerText = ref("");
    const state = ref(null);
    const hasAnswered = ref(false);

    const question = computed(() => questions.value[0]);
    const sanitizedAnswer = computed(() => answerText.value.trim());

    const isEmptyAnswer = computed(() => sanitizedAnswer.value === "");

    const inputStateClass = computed(() => {
      if (state.value === AnswerStateEnum.Correct) {
        return "input--correct";
      }

      if (state.value === AnswerStateEnum.Incorrect) {
        return "input--incorrect";
      }

      return "";
    });

    const isButtonDisabled = computed(() => {
      if (isEmptyAnswer.value) {
        return true;
      }

      if (hasAnswered.value === true) {
        return true;
      }

      return QuizStateEnum.Answered === quizState.value;
    });

    const onAnswer = async () => {
      // Early exit for keyup.enter with empty value.
      if (isEmptyAnswer.value) {
        return;
      }

      const answer = sanitizedAnswer.value;
      hasAnswered.value = true;

      state.value = await props.answerValidator(question.value.id, answer);
      emit("answer", answer);
    };

    return {
      t,
      AnswerStateEnum,

      answerText,
      state,
      promptId,
      hasAnswered,
      onAnswer,
      inputStateClass,
      isButtonDisabled
    };
  }
});
</script>

<template>
  <form class="row writing-game" @submit.prevent="onAnswer()">
    <div class="col-12">
      <div class="input-container">
        <input
          v-model="answerText"
          v-autofocus
          type="text"
          class="input"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          enterkeyhint="done"
          :class="inputStateClass"
          :placeholder="t('vocab.games.writingPlaceholder')"
          :aria-labelledby="promptId"
          :disabled="hasAnswered"
        />
        <Transition name="spiral">
          <IconCorrect
            v-if="state === AnswerStateEnum.Correct"
            class="answer-result-icon answer-result-icon--correct"
          />
          <IconIncorrect
            v-else-if="state === AnswerStateEnum.Incorrect"
            class="answer-result-icon answer-result-icon--incorrect"
          />
        </Transition>
        <span v-if="state === AnswerStateEnum.Correct" class="sr-only" aria-live="polite">
          {{ t("vocab.quizSelectorButton.correct") }}
        </span>
        <span v-else-if="state === AnswerStateEnum.Incorrect" class="sr-only" aria-live="polite">
          {{ t("vocab.quizSelectorButton.incorrect") }}
        </span>
      </div>
    </div>
    <div class="col-12 text-center">
      <TgButton class="submit-button" size="lg" type="submit" :disabled="isButtonDisabled">
        {{ t("vocab.games.confirm") }}
      </TgButton>
    </div>
  </form>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

@icon-size: 38px;

.input-container {
  @grid-gap: 20px;
  display: grid;
  grid-template-columns: @icon-size auto @icon-size;
  align-items: center;
  justify-items: center;
  gap: @grid-gap;
  width: 100%;
  max-width: 375px + (@grid-gap + @icon-size) * 2;
  margin: 0 auto;
}

.input {
  grid-column-start: 2;
  width: 100%;
  padding: 10px 12px;
  transition: all 150ms ease-in-out;
  border: 1px solid rgb(@theme-colors[@grey][base]);
  border-radius: 4px;
  outline: none;
  background: white;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5rem;

  &::placeholder {
    color: rgb(@theme-colors[@grey][50]);
  }

  &:focus {
    border-color: rgb(@theme-colors[@secondary][base]);
    box-shadow: 0px 0px 0px 4px rgba(@theme-colors[@secondary][base], 0.3);
  }

  &--correct {
    border-color: rgb(@theme-colors[@primary][base]);
    box-shadow: 0px 0px 0px 4px rgba(@theme-colors[@primary][base], 0.3);
  }

  &--incorrect {
    border-color: rgb(@theme-colors[@red][base]);
    box-shadow: 0px 0px 0px 4px rgba(@theme-colors[@red][base], 0.3);
  }
}

.answer-result-icon {
  width: @icon-size;
  height: @icon-size;
  margin-right: auto;
}

.answer-result-icon--correct {
  color: rgb(@theme-colors[@primary][50]);
}

.answer-result-icon--incorrect {
  color: rgb(@theme-colors[@red][base]);
}

.submit-button {
  margin-top: 30px;

  #media-breakpoint-up(md, {
      margin-top: 45px;
  });
}

// Animation
.spiral-enter-active {
  transition: all 200ms ease-out;
}

.spiral-leave-active {
  transition: all 200ms cubic-bezier(1, 0.5, 0.8, 1);
}

.spiral-enter-from,
.spiral-leave-to {
  transform: scale(0.8) rotate(180deg);
  opacity: 0;
}
</style>
